/**
 * Waits for the iframe to become visible before rendering.
 * This prevents unnecessary backend calls when the iframe is loaded but hidden.
 *
 * Uses IntersectionObserver to detect when the iframe becomes visible in the viewport.
 */
export async function waitForVisibility(): Promise<void> {
  // Check if IntersectionObserver is supported
  if (!('IntersectionObserver' in window)) {
    // No support, just proceed
    return Promise.resolve()
  }

  return new Promise(resolve => {
    let hasResolved = false

    // Create IntersectionObserver to watch for visibility
    // eslint-disable-next-line compat/compat
    const observer = new IntersectionObserver(
      entries => {
        // Check if the element is visible (intersecting with viewport)
        if (entries[0].isIntersecting && !hasResolved) {
          hasResolved = true
          observer.disconnect()
          resolve()
        }
      },
      { threshold: 0 } // Trigger as soon as any pixel is visible
    )
    const skeletonLoader = document.querySelector('[data-loader]')
    if (skeletonLoader) {
      // Start observing
      observer.observe(skeletonLoader)
    } else {
      // Fallback to observing the root element if the loader element is not found
      const rootElements = document.querySelector('#root')
      if (rootElements) {
        observer.observe(rootElements)
      } else {
        observer.disconnect()
        resolve()
      }
    }
  })
}
